import { useCallback, useEffect, useState } from 'react';
import { useShowFuture } from '../lib/showFuture';
import { api } from '../lib/api';
import { searchTasks } from '../lib/task-search';
import { TaskCardBody } from '../components/TaskCardBody';
import { PickupToggle, usePickupVisible } from '../components/PickupToggle';

/**
 * 可领取 —— 还没有人接的单,司机自己挑。
 *
 * ★ `GRAB` 这个动作、`order:grab` 这个权限、四条可见性守卫建库时就都在了,
 *   **而司机没有任何办法知道有哪些单可以领**。守卫拦得住乱领,
 *   拦不住「看不见」。
 *
 * ★ 这一页上**没有收件人姓名和电话**。他还没接这一单,
 *   凭什么拿到客户的联系方式 —— 地址也按策略脱敏(默认只到路名)。
 *   领了之后才在「今日」里看到全部信息。
 */

interface PoolOrder {
  id: string;
  orderNo: string;
  receiverName: string;
  receiverPhone: string;
  receiverAddress: string;
  pickup: { name: string; address: string; lat: number | null; lng: number | null };
  zone: string | null;
  pickupPoint: string | null;
  packageCount: number;
  weightGram: number | null;
  codAmount: string;
  /** 要代收但金额待定 —— 界面写「联系商家确认」,不写 0 */
  codPending?: boolean;
  payout: string;
  currency: string;
}

/**
 * 抢单关着的时候说清楚是哪一边关的。
 *
 * ★ 「现在没有可领的单」和「本店根本没开抢单」是两件事,
 *   而它们在界面上长得一模一样。前者等一会儿再看,
 *   后者等到明年也不会有单 —— 得有人去把开关打开。
 */
const GRAB_OFF: Record<'FEATURE' | 'POLICY', { title: string; hint: string }> = {
  FEATURE: {
    title: '本店没有开放抢单',
    hint: '所有单都由调度指派。要开的话,让管理员在「设置 → 功能开关 → 抢单池」里打开',
  },
  POLICY: {
    title: '你的账号不能抢单',
    hint: '本店开着抢单池,但你这一类司机的策略里关掉了。找管理员在「设置 → 司机策略」里改',
  },
};

export function Pool({
  keyword,
  grabDisabledBy,
  dutyStatus,
  onGrabbed,
  onDutyChanged,
  onOpenOrder,
}: {
  /**
   * 顶上搜索框里的关键词 —— 在**已经取回来的这一页**里过滤。
   *
   * ★ 不重新发请求:池子是一次取一页的,而司机在这儿搜是为了
   *   「刚看到的那单跑哪去了」—— 就在眼前这一页里。
   */
  keyword: string;
  /** 点一单进详情 —— 和今日列表、地图进的是同一个 */
  onOpenOrder: (orderId: string) => void;
  dutyStatus: string;
  /** 不能抢单时是哪一边关的 —— 决定这一屏说什么 */
  grabDisabledBy: 'FEATURE' | 'POLICY' | null;
  /** 领到一单 —— 带上它的 id,让今日那屏立刻把计数加上,不用等网络 */
  onGrabbed: (orderId: string) => void;
  onDutyChanged: () => void;
}) {
  /* 顶栏那个勾选框 —— 池子和列表、地图共用一份 */
  const [showFuture] = useShowFuture();

  /*
   * ★ 离线时**照样列出来**,但不给点。
   *
   *   「有什么活」是他决定要不要上线的依据,藏起来没道理;
   *   而 GRAB 的守卫要求在线,列了又能点的话他会一直点、一直被拒
   *   —— 这正是这个文件开头那句「列出来一单、点下去被拒,
   *   比不列出来更糟」说的情况,我自己先踩了一遍。
   */
  const offline = dutyStatus === 'OFFLINE';
  const [rows, setRows] = useState<PoolOrder[]>([]);
  /*
   * ★ 搜索在这一栏是**本地过滤**,不重新请求 ——
   *   池子一次就取一页回来,他要找的那单就在眼前这一页里。
   */
  const shown = searchTasks(rows, keyword);
  /** 因为上限没显示出来的有几单 —— 静默截断会让人以为「这片就这些活」 */
  const [hidden, setHidden] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showPickup, setShowPickup] = usePickupVisible();

  const load = useCallback(async () => {
    try {
      const r = await api<{ data: PoolOrder[]; hidden: number }>(
        // 不勾就什么都不传 —— 接口默认只给今天(见 expected-window.ts)
        `/driver/pool${showFuture ? '?includeFuture=1' : ''}`,
      );
      setRows(r.data);
      setHidden(r.hidden);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
    /*
     * ★ `showFuture` 必须在依赖里。少了它,勾选框一拨请求地址虽然变了,
     *   `load` 却还是旧那个闭包 —— 列表停在原来那批上,
     *   要刷新整页才对(用户报的原话:「取消之后并没有立刻刷新数据」)。
     *
     * ★ 时区不用进来了:「今天」现在由**服务端**按租户时区算
     *   (见 expected-window.ts),前端不再需要知道店里在哪个时区。
     */
  }, [showFuture]);

  useEffect(() => {
    void load();
    // 池子是抢的,别人领走了这边要能看出来
    const t = setInterval(() => void load(), 20_000);
    return () => clearInterval(t);
  }, [load]);

  async function goOnline() {
    await api('/driver/me/duty', { method: 'PATCH', body: { dutyStatus: 'ONLINE' } });
    onDutyChanged();
  }

  async function grab(o: PoolOrder) {
    setBusy(o.id);
    setErr(null);
    try {
      await api(`/orders/${o.id}/transitions`, {
        method: 'POST',
        body: { action: 'GRAB', idempotencyKey: `grab-${o.id}-${Date.now()}` },
      });

      /*
       * ★ 领完**留在这一屏**,只把这一条从列表里去掉。
       *
       *   原来是 `setTab('all')` 跳到「全部」再 `await load()` ——
       *   司机想连着领三单,每领一单都被弹走一次,还要等一次网络。
       *   用户原话:「方便用户快速领取多个订单」。
       *
       *   本地先去掉那一条(立刻看得见),今日那屏的计数也当场加一,
       *   真实数据交给后台那次 load —— 它回来之后自然对齐。
       */
      setRows((prev) => prev.filter((r) => r.id !== o.id));
      onGrabbed(o.id);
      // 不 await:这一次刷新是为了对齐,不该让人等着它才能领下一单
      void load();
    } catch (e) {
      /*
       * ★ 领失败最常见的原因是「刚被别人领走了」,这不是错误 ——
       *   直接刷新列表,让他看到那一单没了,比弹一个红框好。
       */
      setErr(e instanceof Error ? e.message : '领取失败');
      await load();
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return <div className="grid h-64 place-items-center text-slate-400">加载中…</div>;
  }

  return (
    <div className="pb-24">
      <header className="px-4 pt-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">可领取</h1>
          {/* 发货那一段默认收起 —— 见 PickupToggle 里为什么是列表级开关 */}
          <PickupToggle on={showPickup} onChange={setShowPickup} />
        </div>
        <p className="mt-0.5 text-sm text-slate-500">
          还没有人接的单。领了之后才看得到收件人信息
        </p>
      </header>

      {offline && (
        <div className="mx-4 mt-3 flex items-center justify-between gap-3 rounded-xl bg-slate-100
                        px-3 py-2 text-sm dark:bg-slate-800">
          <span className="text-slate-600 dark:text-slate-400">先上线才能领单</span>
          <button
            onClick={() => void goOnline()}
            className="h-9 shrink-0 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white"
          >
            上线
          </button>
        </div>
      )}

      {err && (
        <div className="mx-4 mt-3 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800
                        dark:bg-amber-950/40 dark:text-amber-300">
          {err}
        </div>
      )}

      {grabDisabledBy ? (
        <div className="mx-4 mt-6 rounded-2xl border border-dashed border-slate-300 p-8
                        text-center dark:border-slate-700">
          <div className="font-medium text-slate-600 dark:text-slate-300">
            {GRAB_OFF[grabDisabledBy].title}
          </div>
          <div className="mt-1 text-sm text-slate-400">{GRAB_OFF[grabDisabledBy].hint}</div>
        </div>
      ) : shown.length === 0 ? (
        <div className="mx-4 mt-6 rounded-2xl border border-dashed border-slate-300 p-8
                        text-center text-slate-400 dark:border-slate-700">
          {/*
            ★ 「池子是空的」和「搜不到」要分开说。
              都写「现在没有可领的单」的话,他会以为池子空了而其实是
              自己框里还留着上次的关键词 —— 那种误会他很难自己发现。
          */}
          {rows.length === 0 ? '现在没有可领的单' : `可领取里没有「${keyword.trim()}」`}
        </div>
      ) : (
        /*
          ★ 点这一行进详情 —— 和今日列表、地图图钉进的是同一个。
            原来这一页只有「领取」一个按钮:想看清这单从哪到哪、多重多远,
            只能在这张小卡片上找,而详情页里全都有。

          ★ 「领取」要挡住冒泡,否则点它会先把详情打开 ——
            而他要的是领单。(和「当前」那张卡同一个道理。)
        */
        <ul className="mt-3 space-y-2 px-4">
          {shown.map((o) => (
            <li
              key={o.id}
              role="button"
              tabIndex={0}
              aria-label={`打开订单详情:${o.receiverName} ${o.orderNo}`}
              onClick={() => onOpenOrder(o.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOpenOrder(o.id);
                }
              }}
              className="cursor-pointer rounded-2xl border border-slate-200 bg-white p-3
                         dark:border-slate-700 dark:bg-slate-800/60"
            >
              <div className="flex items-start justify-between gap-3">
                <TaskCardBody
                  showPickup={showPickup}
                  data={{
                    receiverName: o.receiverName,
                    receiverPhone: o.receiverPhone,
                    receiverAddress: o.receiverAddress,
                    pickupName: o.pickup?.name ?? o.pickupPoint,
                    pickupAddress: o.pickup?.address,
                    zone: o.zone,
                    packageCount: o.packageCount,
                    weightGram: o.weightGram,
                    codAmount: o.codAmount,
                    codPending: o.codPending,
                    // 给多少是他决定领不领的头一个依据
                    payout: o.payout,
                    currency: o.currency,
                  }}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    void grab(o);
                  }}
                  disabled={busy !== null || offline}
                  className="h-11 shrink-0 rounded-xl bg-brand-600 px-4 text-sm font-medium
                             text-white disabled:opacity-50 active:bg-brand-700"
                >
                  {busy === o.id ? '领取中…' : '领取'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* ★ 上限截断要说出来 —— 不说的话他以为「就这些活」 */}
      {hidden > 0 && (
        <p className="mx-4 mt-3 text-center text-sm text-slate-400">
          还有 {hidden} 单没显示(每次最多列这么多)—— 领掉几单再刷新
        </p>
      )}
    </div>
  );
}
